import React from 'react';
import PropTypes from 'prop-types';
import { Select, Form } from 'antd';
import { countries, getEmojiFlag } from 'countries-list';
import { ChevronDown, ChevronUp } from '../../assets/icons';
import FloatingLabel from '../FloatingLabel/FloatingLabel';
import './CountryDropdown.scss';
import { normalizePhoneCode } from '../../utils/normalizePhoneCode';

const CountryDropdown = (props) => {
  const { setCountry, country, isEdit, required, label = 'Country' } = props;
  const [arrowUp, setArrowUp] = React.useState(false);

  // Convert countries object to array with code and country data
  const countriesArray = React.useMemo(() => {
    // Map the countries to a standard structure
    const baseCountries = Object.entries(countries).map(([code, countryData]) => ({
      code,
      name: countryData.name,
      phone: Array.isArray(countryData.phone)
        ? countryData.phone[0] // pick the first, but we'll override XK below
        : countryData.phone,
      flag: getEmojiFlag(code),
    }));

    // Filter out XK if it exists (to override it)
    const filteredCountries = baseCountries.filter((c) => c.code !== 'XK');

    // Add XK with correct phone
    filteredCountries.push({
      code: 'XK',
      name: 'Kosovo',
      phone: '383',
      flag: '🇽🇰',
    });

    return filteredCountries.sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  // Set default to US if not provided
  const defaultValue = country || 'US';

  // Set US as default on mount if no country selected
  React.useEffect(() => {
    if (!country) {
      setCountry('US');
    }
  }, []);

  const handleCountryChange = (code) => {
    const selectedCountry = countriesArray.find(c => c.code === code);
    if (selectedCountry) {
      // Pass phone as a string, not an array
      setCountry(selectedCountry.code, selectedCountry.phone);
    }
  };

  return (
    <FloatingLabel label={label} inputId='country' isEdit={isEdit} name='identification' inputValue={defaultValue}>
      <>
        <div id='country-dropdown-anchor' />
        <Form.Item name='country' rules={[{ required: required, message: 'Please select country' }]} initialValue="US">
          <Select
            id='country'
            className='country-select'
            showSearch
            placeholder="Select a country"
            filterOption={(input, option) => {
              if (!option || !option.value) return false;

              // Find the country data for this option
              const currentCountry = countriesArray.find(c => c.code === option.value);
              if (!currentCountry) return false;

              const searchText = input.toLowerCase();
              const countryName = currentCountry.name.toLowerCase();
              const countryCode = currentCountry.code.toLowerCase();
              const phoneCode = String(currentCountry.phone);
              return countryName.includes(searchText) ||
                countryCode.includes(searchText) ||
                phoneCode.includes(searchText);
            }}
            onChange={handleCountryChange}
            value={defaultValue}
            getPopupContainer={() => document.getElementById('country-dropdown-anchor')}
            onDropdownVisibleChange={() => setArrowUp(!arrowUp)}
            suffixIcon={arrowUp ? <ChevronUp color='#1A1519' /> : <ChevronDown color='#1A1519' />}
          >
            {countriesArray.map((country) => { 
              return (
                <Select.Option key={country.code} value={country.code}>
                  <span style={{ marginRight: '10px', fontSize: '16px' }}>
                    {country.flag}
                  </span>
                  <span style={{ fontWeight: '500' }}>{country.code} (+{normalizePhoneCode(country.phone)})</span>
                </Select.Option>
              );
            })}
          </Select>
        </Form.Item>
      </>
    </FloatingLabel>
  );
};

CountryDropdown.propTypes = {
  setCountry: PropTypes.func.isRequired,
  country: PropTypes.string,
  isEdit: PropTypes.bool,
  required: PropTypes.bool,
  label: PropTypes.string
};

export default CountryDropdown;